<template>
  <NLayout
    ref="contentRef"
    class="canvas-builder-layout !bg-base"
    :class="{ 'flex flex-col flex-auto': isScaleContentEnabled }"
    :content-class="contentClass"
  >
    <NLayoutContent
      class="flex justify-center canvas-builder-layout__content relative break-words"
      :class="{
        'min-h-[100%]': !isEditMode && !isScaleContentEnabled,
        'flex flex-col flex-auto items-center': isScaleContentEnabled,
      }"
      :content-class="contentClass"
      :content-style="{
        maxWidth: getMaxWidth(
          pitcherSettings,
          !!launchDarkly.enable_scale_content,
          !!launchDarkly.enable_canvas_pdf_dimensions,
        ),
        width: '100%',
        ...activeCanvas?.theme?.style,
      }"
    >
      <CButton
        v-if="showExtraExitBtn"
        v-tooltip="t('canvasUI.canvasBuilder.content.exit')"
        class="fixed w-11 h-11 top-4 left-4 z-999999 opacity-60"
        size="large"
        strong
        type="primary"
        @click.stop="onExitClick"
      >
        <template #icon>
          <CIcon class="fa-flip-horizontal" fa-type="fas" icon="right-from-bracket" size="18" />
        </template>
      </CButton>
      <video
        v-if="videoUrl"
        autoplay
        class="absolute top-0 left-0 w-full h-full object-cover z-0"
        loop
        muted
        :src="videoUrl"
      />

      <template v-for="app in filteredCanvasHookApps" :key="app.name">
        <iframe
          v-if="app.content_url"
          :ref="(el) => setCanvasHookRefs(el as HTMLIFrameElement, app)"
          height="0"
          :src="getEntryForSectionHook(app)"
          style="border: none; display: block"
          :title="app.name"
        />
      </template>

      <div
        class="components-content"
        :class="{
          'flex flex-col': isDefaultDisplayMode,
          'overflow-hidden': !!animationDirection,
          'flex flex-col flex-auto overflow-hidden': !isEditMode && isScaleContentEnabled && isDefaultDisplayMode,
          'pt-3': isEditMode,
        }"
      >
        <CanvasContentPaginated
          v-if="isPresentationWithNoCollapsibles"
          :is-scale-content-enabled="isScaleContentEnabled"
        />

        <CanvasContentSingle v-else />
      </div>

      <div
        v-if="isEditMode"
        class="add-at-root-placeholder flex justify-center items-center b-rounded-2 cursor-pointer mb-3"
        :class="{
          'add-at-root-placeholder--active': addMode && !componentSelectedMode && addMode.type === ComponentTypes.Root,
          'mt-8': canvasContent.length,
        }"
        @click="onRootAdd"
      >
        <div class="pa-12">
          <CButton :disabled="isViewOnlyMode" iconPlacement="left" round type="primary">
            <template #icon>
              <CIcon icon="plus-large" size="18" />
            </template>
            <span>{{ t('canvasUI.canvasBuilder.content.addToCanvas') }}</span>
          </CButton>
        </div>
      </div>

      <AnnotationContent
        v-if="!isEditMode && !isDsr && contentAnnotation && pitcherSettings.enable_presentation_annotations"
        class="z-9"
      />

      <BottomBar
        v-if="isFullscreen && (!pitcherSettings.alternative_canvas_fullscreen || isCanvasPreviewMode)"
        class="fixed left-0 right-0 bottom-3 mx-8 z-10"
      />
      <NModal
        v-if="selectedApp && selectedAppUrl"
        preset="card"
        show
        :style="getModalStyle"
        :title="selectedApp?.app_metadata?.display_name || selectedApp?.name || ''"
        to="body"
        @close="closeAppModal"
      >
        <iframe ref="appIframeRef" class="w-full h-full border-none" :src="selectedAppUrl" />
      </NModal>
    </NLayoutContent>
  </NLayout>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, type ComputedRef, watch, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { NModal, NLayout, NLayoutContent } from 'naive-ui'

import useThemeVars from '@lib/composables/useThemeVars'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import { CanvasBuilderMode, ComponentTypes } from '@canvas-builder/types/canvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import BottomBar from '@canvas-builder/components/container/BottomBar/BottomBar.vue'
import useCanvasHooks from '@canvas-builder/composables/useCanvasHooks'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'
import { type AppFile } from '@lib/types/app'
import { deepToRaw } from '@lib/util/reactive'
import { type CanvasRetrieve } from '@openapi'
import { CANVAS_HOOKS } from '@lib/types/iframes'
import CanvasContentPaginated from './CanvasContent.paginated.vue'
import CanvasContentSingle from './CanvasContent.single.vue'
import useCanvasExit from '@canvas-builder/composables/useCanvasExit'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import useEmbeddable from '@canvas-builder/composables/useEmbeddable'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import { usePopupApps } from '@canvas-builder/composables/usePopupApps'
import AnnotationContent from '@canvas-builder/components/container/CanvasContent/AnnotationContent.vue'
import { convertToPixels } from '@canvas-builder/util/print.util'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const canvasHookApps = inject<ComputedRef<AppFile[]>>(
  'canvasHookApps',
  computed(() => []),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const themeVars = useThemeVars()
const { t } = useI18n()
const { canvasBgColor, font, isFluid, maintainSectionTheme } = useCanvasTheme()
const { updateEmbeddableHeights } = useEmbeddable()
const {
  isEditMode,
  isDsr,
  canvasContent,
  addMode,
  componentSelectedMode,
  isViewOnlyMode,
  contentRef,
  isDefaultDisplayMode,
  isFullscreen,
  isCanvasPreviewMode,
  activeCanvas,
  isPrintDisplayMode,
  mode,
  contentAnnotation,
  setComponentSelectedMode,
  setAddMode,
  getMaxWidth,
} = useCanvas()
const { onExitClick } = useCanvasExit()
const { activeSectionTheme, animationDirection, keepActivePageValid } = useCanvasPages()
keepActivePageValid()

const { getEntryForSectionHook, setHookRefs, onCanvasUpdatedHook, cleanup: cleanupCanvasHook } = useCanvasHooks()
const { fetchPopularityData } = usePopularity()
const { isCompletionWizardShowing } = useCanvasCompletionWizard()
const { selectedApp, selectedAppUrl, appIframeRef, getModalStyle, closeAppModal } = usePopupApps()

// Get PDF dimensions from canvas theme or organization settings
const pdfDimensions = computed(() => {
  if (!launchDarkly.value.enable_scale_content) {
    return null
  }

  // Use canvas-specific PDF dimensions if set, otherwise fall back to pitcher settings default
  const canvasPdfWidth =
    launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.width
  const canvasPdfHeight =
    launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.height

  const pdfWidth = canvasPdfWidth || pitcherSettings.value.default_pdf_page_dimensions?.width
  const pdfHeight = canvasPdfHeight || pitcherSettings.value.default_pdf_page_dimensions?.height

  if (!pdfWidth || !pdfHeight) {
    return null
  }

  const widthPx = convertToPixels(pdfWidth)
  const heightPx = convertToPixels(pdfHeight)

  if (!widthPx || !heightPx || widthPx <= 0 || heightPx <= 0) {
    return null
  }

  return { widthPx, heightPx }
})

const showExtraExitBtn = computed(
  () =>
    ((pitcherSettings.value.canvas?.show_extra_exit_button_in_fullscreen_mode &&
      isFullscreen.value &&
      !pitcherSettings.value.alternative_canvas_fullscreen) ||
      (pitcherSettings.value.alternative_canvas_fullscreen && isFullscreen.value)) &&
    mode.value !== CanvasBuilderMode.SHARING &&
    !isCompletionWizardShowing.value &&
    !isCanvasPreviewMode.value,
)
const canvasTheme = computed(() => ({
  bgColor: canvasBgColor.value || themeVars.value.base,
}))

const themeBgColor = computed(() => {
  const shouldUseSectionTheme =
    (maintainSectionTheme.value &&
      !activeSectionTheme.value?.inherit_theme_from_parent &&
      !isEditMode.value &&
      (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' &&
      !isPrintDisplayMode.value) ||
    (isPrintDisplayMode.value && launchDarkly.value?.enable_scale_content)

  return shouldUseSectionTheme
    ? activeSectionTheme.value?.canvas_bg_color || canvasTheme.value.bgColor
    : canvasTheme.value.bgColor
})

const videoUrl = computed(() => activeCanvas.value?.theme?.background_video_url)

const isPresentationWithNoCollapsibles = computed(
  () => (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' && !isEditMode.value,
)

const filteredCanvasHookApps = computed(() => {
  return canvasHookApps.value?.filter?.((a: AppFile) => a.app_metadata?.hooks?.canvas?.on_canvas_update) ?? []
})

function setCanvasHookRefs(iframeEl: HTMLIFrameElement | null, app: AppFile) {
  if (iframeEl && app) {
    iframeEl.onload = () => {
      if (activeCanvas.value) {
        onCanvasUpdatedHook({
          hookIframes: { [app.id]: iframeEl },
          activeCanvas: deepToRaw(activeCanvas.value) as CanvasRetrieve,
        })
      }
    }

    setHookRefs(iframeEl, app, CANVAS_HOOKS.ON_CANVAS_UPDATE)
  }
}

const onRootAdd = () => {
  if (isViewOnlyMode.value) return
  setAddMode(ComponentTypes.Root)
  setComponentSelectedMode() // clear selected component
}

const isScaleEnabled = computed(
  () =>
    !!launchDarkly.value.enable_scale_content &&
    isFluid.value &&
    activeCanvas.value?.theme?.aspect_ratio &&
    activeCanvas.value?.theme?.aspect_ratio !== 'unlocked',
)

const isScaleContentEnabled = computed(() => isScaleEnabled.value && !isEditMode.value)

provide('isScaleEnabled', isScaleEnabled)

const contentClass = computed(() => {
  if (isScaleContentEnabled.value) {
    return 'flex flex-col flex-auto'
  }

  return undefined
})

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons && mode.value !== 'sharing') {
    fetchPopularityData(EntityType.FILE)
  }

  if (launchDarkly.value.enable_more_intuitive_component_spacing) {
    updateEmbeddableHeights()
  }

  watch(
    [isPrintDisplayMode, pdfDimensions],
    ([isPrinting, dimensions]) => {
      if (isPrinting && dimensions) {
        let styleEl = document.getElementById('print-orientation-style')
        if (!styleEl) {
          styleEl = document.createElement('style')
          styleEl.id = 'print-orientation-style'
          document.head.appendChild(styleEl)
        }

        // Always use explicit dimensions (orientation is implicit from which dimension is larger)
        styleEl.textContent = `
          @page {
            size: ${dimensions.widthPx}px ${dimensions.heightPx}px !important;
          }
        `
      }
    },
    { immediate: true },
  )
})

onUnmounted(() => {
  for (const app of filteredCanvasHookApps.value) {
    cleanupCanvasHook(app, CANVAS_HOOKS.ON_CANVAS_UPDATE)
  }
})
</script>

<style lang="scss" scoped>
.canvas-builder-layout,
.canvas-builder-layout__content {
  background-color: v-bind('themeBgColor');
}

// TODO: remove this once we add the default font to the theme
// .canvas-builder-layout:not(.canvas-builder-layout--canvas-tokens-enabled),
// .canvas-builder-layout:not(.canvas-builder-layout--canvas-tokens-enabled) .canvas-builder-layout__content {
//   font-family: v-bind('font');
// }

.canvas-builder-layout,
.canvas-builder-layout .canvas-builder-layout__content {
  font-family: v-bind('font');
}

.add-at-root-placeholder {
  font-family: var(--font-family);
  border: 1px solid v-bind('themeVars.primary5');

  &--active {
    border-color: v-bind('themeVars.info');
  }
}
</style>
